import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

const STORAGE_KEY = 'singleActiveDashboardTabLock';
const HEARTBEAT_MS = 2000;
const STALE_MS = 5000;

const createTabId = () =>
  `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const safeParse = value => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const isFreshLock = (lock, now, staleMs) => {
  return !!lock && Number(lock.heartbeatAt) + staleMs > now();
};

export const createSingleActiveTabManager = ({
  onStateChange = () => {},
  heartbeatMs = HEARTBEAT_MS,
  staleMs = STALE_MS,
  win = window,
  doc = document,
  now = () => Date.now(),
  session = window.sessionStorage,
  storage = window.localStorage,
} = {}) => {
  let heartbeatTimer = null;
  let monitorTimer = null;
  let isStarted = false;
  let isBlocked = false;
  let isOwner = false;

  const tabId =
    session.getItem(SESSION_STORAGE_KEYS.DASHBOARD_TAB_ID) || createTabId();

  session.setItem(SESSION_STORAGE_KEYS.DASHBOARD_TAB_ID, tabId);

  const getLock = () => safeParse(storage.getItem(STORAGE_KEY));

  const emitState = () => {
    onStateChange({
      isBlocked,
      isOwner,
      tabId,
    });
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      win.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const writeLock = () => {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        tabId,
        heartbeatAt: now(),
      })
    );
  };

  const refreshOwnership = () => {
    const lock = getLock();

    if (lock?.tabId !== tabId) {
      stopHeartbeat();
      isOwner = false;
      return;
    }

    writeLock();
    isOwner = true;
    isBlocked = false;
    emitState();
  };

  const ensureHeartbeat = () => {
    if (heartbeatTimer) {
      return;
    }

    heartbeatTimer = win.setInterval(refreshOwnership, heartbeatMs);
  };

  const releaseLock = () => {
    const lock = getLock();

    if (lock?.tabId === tabId) {
      storage.removeItem(STORAGE_KEY);
    }

    stopHeartbeat();
    isOwner = false;
  };

  const claim = ({ force = false } = {}) => {
    const lock = getLock();
    const hasFreshOwner = isFreshLock(lock, now, staleMs);

    if (!force && hasFreshOwner && lock?.tabId !== tabId) {
      isBlocked = true;
      isOwner = false;
      stopHeartbeat();
      emitState();
      return false;
    }

    writeLock();
    isBlocked = false;
    isOwner = true;
    ensureHeartbeat();
    emitState();
    return true;
  };

  const syncFromStorage = () => {
    const lock = getLock();
    const hasFreshOwner = isFreshLock(lock, now, staleMs);

    if (!hasFreshOwner) {
      if (doc.visibilityState === 'visible') {
        claim();
      } else {
        isBlocked = false;
        isOwner = false;
        stopHeartbeat();
        emitState();
      }
      return;
    }

    if (lock?.tabId === tabId) {
      isBlocked = false;
      isOwner = true;
      ensureHeartbeat();
      emitState();
      return;
    }

    isBlocked = true;
    isOwner = false;
    stopHeartbeat();
    emitState();
  };

  const handleStorage = event => {
    if (event.key === STORAGE_KEY) {
      syncFromStorage();
    }
  };

  const handleVisibilityChange = () => {
    if (doc.visibilityState === 'visible') {
      syncFromStorage();
    }
  };

  const handleBeforeUnload = () => {
    releaseLock();
  };

  return {
    start() {
      if (isStarted) {
        return;
      }

      isStarted = true;
      claim();
      monitorTimer = win.setInterval(syncFromStorage, heartbeatMs);
      win.addEventListener('storage', handleStorage);
      win.addEventListener('beforeunload', handleBeforeUnload);
      doc.addEventListener('visibilitychange', handleVisibilityChange);
    },
    stop() {
      if (!isStarted) {
        return;
      }

      isStarted = false;
      releaseLock();

      if (monitorTimer) {
        win.clearInterval(monitorTimer);
        monitorTimer = null;
      }

      win.removeEventListener('storage', handleStorage);
      win.removeEventListener('beforeunload', handleBeforeUnload);
      doc.removeEventListener('visibilitychange', handleVisibilityChange);
      isBlocked = false;
      isOwner = false;
      emitState();
    },
    takeOver() {
      claim({ force: true });
    },
  };
};
